import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import useScreenSize from '../../hooks/useScreenSize';

const VoiceRecorder = ({ isOpen, onClose, onTranscriptionComplete }) => {
  const { isMobile } = useScreenSize();
  const [isRecording, setIsRecording] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  const [currentChunkDuration, setCurrentChunkDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [currentChunkNumber, setCurrentChunkNumber] = useState(1);
  const [transcribingChunks, setTranscribingChunks] = useState([]);
  const [completedTranscriptions, setCompletedTranscriptions] = useState([]);

  const mediaRecorderRef = useRef(null);
  const currentChunkDataRef = useRef([]);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const chunkStartTimeRef = useRef(0);
  const transcribingChunksRef = useRef([]);
  const completedTranscriptionsRef = useRef([]);

  const CHUNK_DURATION = 480; // 8 minutes per chunk (safe margin under 25MB)
  const MAX_SIZE = 25 * 1024 * 1024; // 25MB

  // Check if recording is supported
  const isRecordingSupported = () => {
    try {
      const hasMediaDevices = typeof navigator !== 'undefined' &&
                              navigator.mediaDevices &&
                              typeof navigator.mediaDevices.getUserMedia === 'function';
      const hasMediaRecorder = typeof window !== 'undefined' &&
                               typeof window.MediaRecorder === 'function';

      const isSupported = hasMediaDevices && hasMediaRecorder;

      // Debug logging
      console.log('Voice Recorder Support Check:', {
        hasMediaDevices,
        hasMediaRecorder,
        isSupported,
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        isLocalhost: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      });

      return isSupported;
    } catch (err) {
      console.error('Error checking recording support:', err);
      return false;
    }
  };

  // Format duration as mm:ss
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize audio visualization
  const initializeAudioVisualization = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start visualization loop
      const updateAudioLevel = () => {
        if (!analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average volume
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(Math.min(100, (average / 128) * 100));

        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };

      updateAudioLevel();
    } catch (err) {
      console.error('Audio visualization error:', err);
    }
  };

  // Transcribe a chunk in the background
  const transcribeChunk = async (audioBlob, chunkNum) => {
    const chunkId = `chunk-${chunkNum}`;

    // Add to transcribing list
    setTranscribingChunks(prev => {
      const updated = [...prev, { id: chunkId, number: chunkNum, status: 'transcribing' }];
      transcribingChunksRef.current = updated;
      return updated;
    });

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('audio_file', audioBlob, `recording-chunk-${chunkNum}.webm`);
      formData.append('language', 'en');

      // Send to backend
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.post(`${API_URL}/ai/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        timeout: 60000 // 60 seconds timeout
      });

      if (response.data.success) {
        // Remove from transcribing list
        setTranscribingChunks(prev => {
          const updated = prev.filter(c => c.id !== chunkId);
          transcribingChunksRef.current = updated;
          return updated;
        });

        // Add to completed transcriptions
        setCompletedTranscriptions(prev => {
          const updated = [...prev, { number: chunkNum, text: response.data.text }];
          completedTranscriptionsRef.current = updated;
          return updated;
        });

        return { success: true, text: response.data.text };
      } else {
        throw new Error(response.data.error || 'Transcription failed');
      }
    } catch (err) {
      console.error(`Chunk ${chunkNum} transcription error:`, err);

      // Update status to failed
      setTranscribingChunks(prev => {
        const updated = prev.map(c =>
          c.id === chunkId ? { ...c, status: 'failed', error: err.message } : c
        );
        transcribingChunksRef.current = updated;
        return updated;
      });

      return { success: false, error: err.message };
    }
  };

  // Process current chunk and start new one
  const processCurrentChunk = useCallback(() => {
    if (currentChunkDataRef.current.length === 0) return;

    // Create blob from current chunk
    const audioBlob = new Blob(currentChunkDataRef.current, { type: 'audio/webm' });
    const chunkNum = currentChunkNumber;

    // Start transcription in background
    transcribeChunk(audioBlob, chunkNum);

    // Reset current chunk data for next chunk
    currentChunkDataRef.current = [];
    setCurrentChunkNumber(prev => prev + 1);
    setCurrentChunkDuration(0);
    chunkStartTimeRef.current = Date.now();
  }, [currentChunkNumber]);

  // Start recording
  const startRecording = async () => {
    if (!isRecordingSupported()) {
      const isSecure = window.location.protocol === 'https:' ||
                      window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

      if (!isSecure) {
        setError('Recording requires HTTPS or localhost. Please access via https:// or http://localhost');
      } else {
        setError('Recording is not supported in this browser. Please use Chrome, Firefox, Edge, or Safari.');
      }
      return;
    }

    try {
      setError(null);
      setPermissionDenied(false);
      setCurrentChunkNumber(1);
      setCompletedTranscriptions([]);
      setTranscribingChunks([]);
      completedTranscriptionsRef.current = [];
      transcribingChunksRef.current = [];

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1, // Mono
          sampleRate: 16000, // 16kHz
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      streamRef.current = stream;
      initializeAudioVisualization(stream);

      // Create MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      currentChunkDataRef.current = [];
      chunkStartTimeRef.current = Date.now();

      // Collect audio data
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          currentChunkDataRef.current.push(event.data);
        }
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every 1 second
      setIsRecording(true);

      // Start timer
      timerRef.current = setInterval(() => {
        setTotalDuration(prev => prev + 1);
        setCurrentChunkDuration(prev => {
          const newDuration = prev + 1;

          // Auto-process chunk at CHUNK_DURATION
          if (newDuration >= CHUNK_DURATION) {
            // Stop current recorder
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();

              // Wait a bit for ondataavailable to complete, then process chunk
              setTimeout(() => {
                processCurrentChunk();

                // Start new chunk
                if (streamRef.current) {
                  const newRecorder = new MediaRecorder(streamRef.current, { mimeType });
                  newRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                      currentChunkDataRef.current.push(event.data);
                    }
                  };
                  newRecorder.start(1000);
                  mediaRecorderRef.current = newRecorder;
                }
              }, 100);
            }

            return 0;
          }

          return newDuration;
        });
      }, 1000);

    } catch (err) {
      console.error('Error starting recording:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
        setError('Microphone permission denied. Please enable microphone access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone detected. Please connect a microphone and try again.');
      } else {
        setError(`Failed to start recording: ${err.message}`);
      }
    }
  };

  // Stop recording and finalize
  const stopRecording = async () => {
    if (!mediaRecorderRef.current || !isRecording) return;

    setIsRecording(false);
    setIsFinalizing(true);

    // Stop current recorder
    if (mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop visualization
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    // Wait a bit for last data chunk, then process final chunk
    setTimeout(async () => {
      // Process the final chunk if it has data
      if (currentChunkDataRef.current.length > 0) {
        const audioBlob = new Blob(currentChunkDataRef.current, { type: 'audio/webm' });
        await transcribeChunk(audioBlob, currentChunkNumber);
      }

      // Wait for all transcriptions to complete
      const maxWaitTime = 120000; // 2 minutes max wait
      const startWaitTime = Date.now();

      const waitForTranscriptions = setInterval(() => {
        // Check if all done or timeout (use refs for current values)
        const allDone = transcribingChunksRef.current.length === 0 ||
                       transcribingChunksRef.current.every(c => c.status === 'failed');
        const timedOut = Date.now() - startWaitTime > maxWaitTime;

        if (allDone || timedOut) {
          clearInterval(waitForTranscriptions);

          // Combine all transcriptions in order (use ref for current values)
          const sortedTranscriptions = [...completedTranscriptionsRef.current].sort((a, b) => a.number - b.number);
          const combinedText = sortedTranscriptions.map(t => t.text).join('\n\n');

          if (combinedText) {
            // Call parent callback with combined text
            if (onTranscriptionComplete) {
              onTranscriptionComplete(combinedText);
            }
            handleClose();
          } else {
            setError('No transcriptions completed successfully');
            setIsFinalizing(false);
          }
        }
      }, 500);
    }, 500);
  };

  // Cancel recording
  const cancelRecording = () => {
    if (isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop visualization
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    handleClose();
  };

  // Handle close
  const handleClose = () => {
    // Reset state
    setIsRecording(false);
    setTotalDuration(0);
    setCurrentChunkDuration(0);
    setAudioLevel(0);
    setIsFinalizing(false);
    setError(null);
    setPermissionDenied(false);
    setCurrentChunkNumber(1);
    setTranscribingChunks([]);
    setCompletedTranscriptions([]);
    currentChunkDataRef.current = [];
    transcribingChunksRef.current = [];
    completedTranscriptionsRef.current = [];

    // Call parent onClose
    if (onClose) {
      onClose();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isRecording && !isFinalizing) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isRecording, isFinalizing]);

  if (!isOpen) return null;

  const failedChunks = transcribingChunks.filter(c => c.status === 'failed');

  return (
    <div style={styles.overlay} onClick={(e) => {
      if (e.target === e.currentTarget && !isRecording && !isFinalizing) {
        cancelRecording();
      }
    }}>
      <div
        style={{
          ...styles.modal,
          width: isMobile ? '95%' : '90%',
          maxWidth: isMobile ? 'none' : '500px',
          padding: isMobile ? '24px' : '32px',
        }}
      >
        <h2 style={styles.title}>🎤 Voice Recording</h2>

        {/* Chunk Info */}
        {isRecording && (
          <div style={styles.chunkInfo}>
            Chunk {currentChunkNumber} • {formatDuration(currentChunkDuration)} / {formatDuration(CHUNK_DURATION)}
          </div>
        )}

        {/* Audio Level Visualization */}
        {isRecording && (
          <div style={styles.visualizationContainer}>
            <div style={styles.visualizationBar}>
              <div
                style={{
                  ...styles.visualizationLevel,
                  width: `${audioLevel}%`
                }}
              />
            </div>
          </div>
        )}

        {/* Total Timer */}
        <div style={styles.timer}>
          {formatDuration(totalDuration)}
        </div>

        {/* Background Transcriptions Status */}
        {transcribingChunks.length > 0 && (
          <div style={styles.transcriptionStatus}>
            <div style={styles.statusHeader}>Background Transcription:</div>
            {transcribingChunks.map(chunk => (
              <div key={chunk.id} style={styles.statusItem}>
                {chunk.status === 'transcribing' && (
                  <>
                    <div style={styles.miniSpinner} />
                    Chunk {chunk.number} transcribing...
                  </>
                )}
                {chunk.status === 'failed' && (
                  <>
                    ❌ Chunk {chunk.number} failed
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Completed Count */}
        {completedTranscriptions.length > 0 && (
          <div style={styles.completedInfo}>
            ✓ {completedTranscriptions.length} chunk{completedTranscriptions.length !== 1 ? 's' : ''} transcribed
          </div>
        )}

        {/* Status */}
        {isFinalizing && (
          <div style={styles.status}>
            <div style={styles.spinner} />
            Finalizing transcription... Please wait.
          </div>
        )}

        {isRecording && !isFinalizing && (
          <div style={styles.recordingIndicator}>
            <span style={styles.recordingDot} />
            Recording... (No time limit - auto-chunks every 8 minutes)
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={styles.errorBox}>
            {error}
            {permissionDenied && (
              <div style={styles.errorHelp}>
                <strong>How to enable microphone:</strong>
                <ul style={styles.helpList}>
                  <li>Click the 🔒 or 🛡️ icon in your browser's address bar</li>
                  <li>Find "Microphone" permissions</li>
                  <li>Select "Allow" and refresh the page</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Failed Chunks Warning */}
        {failedChunks.length > 0 && !isFinalizing && (
          <div style={styles.warningBox}>
            ⚠️ {failedChunks.length} chunk(s) failed to transcribe
          </div>
        )}

        {/* Controls */}
        <div style={styles.controls}>
          {!isRecording && !isFinalizing ? (
            <>
              <button
                type="button"
                onClick={startRecording}
                className="internal-primary-action"
                style={{
                  ...styles.button,
                  ...styles.startButton,
                  padding: isMobile ? '14px 24px' : '12px 28px',
                  minHeight: isMobile ? '48px' : 'auto',
                }}
                disabled={!isRecordingSupported()}
              >
                {isRecordingSupported() ? 'Start Recording' : 'Recording Not Supported'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="internal-secondary-action"
                style={{
                  ...styles.button,
                  ...styles.cancelButton,
                  padding: isMobile ? '14px 24px' : '12px 28px',
                  minHeight: isMobile ? '48px' : 'auto',
                }}
              >
                Close
              </button>
            </>
          ) : isRecording ? (
            <>
              <button
                type="button"
                onClick={stopRecording}
                className="destructive-action"
                style={{
                  ...styles.button,
                  ...styles.stopButton,
                  padding: isMobile ? '14px 24px' : '12px 28px',
                  minHeight: isMobile ? '48px' : 'auto',
                }}
              >
                Stop & Transcribe
              </button>
              <button
                type="button"
                onClick={cancelRecording}
                className="internal-secondary-action"
                style={{
                  ...styles.button,
                  ...styles.cancelButton,
                  padding: isMobile ? '14px 24px' : '12px 28px',
                  minHeight: isMobile ? '48px' : 'auto',
                }}
              >
                Cancel
              </button>
            </>
          ) : null}
        </div>

        {!isRecordingSupported() && (
          <div style={styles.notSupported}>
            <strong>Audio Recording Not Supported</strong>
            <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>
              Your browser doesn't support audio recording. Please try:
            </p>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
              <li>Using Chrome, Firefox, Edge, or Safari</li>
              <li>Accessing via HTTPS (required for microphone access)</li>
              <li>Updating your browser to the latest version</li>
            </ul>
            <details style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
              <summary style={{ cursor: 'pointer' }}>Debug Info</summary>
              <div style={{ marginTop: '8px', fontFamily: 'monospace' }}>
                <div>navigator.mediaDevices: {String(!!navigator.mediaDevices)}</div>
                <div>getUserMedia: {String(!!(navigator.mediaDevices?.getUserMedia))}</div>
                <div>MediaRecorder: {String(!!(window.MediaRecorder))}</div>
                <div>HTTPS: {String(window.location.protocol === 'https:')}</div>
              </div>
            </details>
          </div>
        )}

        {/* Info text */}
        {!isRecording && !isFinalizing && isRecordingSupported() && (
          <div style={styles.infoText}>
            💡 Record for any duration - the system automatically processes 8-minute chunks in the background.
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },
  title: {
    margin: '0 0 24px 0',
    fontSize: '24px',
    fontWeight: '600',
    textAlign: 'center',
    background: '#0778ad',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  chunkInfo: {
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '12px',
    color: '#666',
    fontFamily: 'monospace',
  },
  visualizationContainer: {
    marginBottom: '20px',
  },
  visualizationBar: {
    width: '100%',
    height: '40px',
    background: '#f0f0f0',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
  },
  visualizationLevel: {
    height: '100%',
    background: '#0778ad',
    transition: 'width 0.1s ease',
    borderRadius: '8px',
  },
  timer: {
    fontSize: '48px',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '12px',
    fontFamily: 'monospace',
    color: '#333',
  },
  transcriptionStatus: {
    background: '#f0f8ff',
    border: '1px solid #b3d9ff',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
    fontSize: '13px',
  },
  statusHeader: {
    fontWeight: '600',
    marginBottom: '8px',
    color: '#0066cc',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    color: '#666',
  },
  miniSpinner: {
    width: '12px',
    height: '12px',
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #0778ad',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  completedInfo: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#22c55e',
    marginBottom: '12px',
    fontWeight: '500',
  },
  status: {
    textAlign: 'center',
    padding: '16px',
    background: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '16px',
    color: '#0778ad',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #0778ad',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  recordingIndicator: {
    textAlign: 'center',
    padding: '12px',
    background: '#fff3f3',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    color: '#ff4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexDirection: 'column',
  },
  recordingDot: {
    width: '12px',
    height: '12px',
    background: '#ff4444',
    borderRadius: '50%',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  errorBox: {
    background: '#fff0f0',
    border: '1px solid #ffcccc',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    color: '#d63031',
    fontSize: '14px',
  },
  errorHelp: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#666',
  },
  helpList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
  },
  warningBox: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
    color: '#f57c00',
    fontSize: '14px',
    textAlign: 'center',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  button: {
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
    maxWidth: '200px',
  },
  startButton: {
    background: '#0778ad',
    color: 'white',
    boxShadow: '0 4px 12px rgba(8, 126, 184, 0.25)',
  },
  stopButton: {
    background: '#b42318',
    color: 'white',
    boxShadow: '0 4px 12px rgba(255, 68, 68, 0.3)',
  },
  cancelButton: {
    background: '#f0f0f0',
    color: '#334e68',
    border: '1px solid #c7dce8',
  },
  notSupported: {
    marginTop: '16px',
    padding: '12px',
    background: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#856404',
    textAlign: 'center',
  },
  infoText: {
    marginTop: '16px',
    padding: '12px',
    background: '#f0f8ff',
    border: '1px solid #b3d9ff',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#0066cc',
    textAlign: 'center',
  },
};

// Add CSS animation for spinner and pulse
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }
`;
document.head.appendChild(styleSheet);

export default VoiceRecorder;
